import { useMutation } from '@tanstack/react-query';
import Decimal from 'decimal.js-light';
import { formatEther } from 'viem';

import { BalanceFraction } from '@/components/TransactionModule/SellTokenQuickActions';

import type { SwapPrice } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { getTokenPair } from '@/utils/amplitude';
import { computeNativeFeeReserveWei } from '@/utils/fees';
import { zeroExApiFetcher } from '@/utils/fetcher';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

type Params = {
  buyToken: TokenResult | undefined;
  sellToken: TokenResult | undefined;
  chainId: number;
  slippage: number | undefined;
  excludedSources: string | undefined;
  accountAddress: ChainAddress | undefined;
  buyTokenAmountUsd: number | undefined;
};

export type EstimateNativeFeeReserveVariables = {
  /** Raw sell amount (in wei) we want to price-quote for fee estimation. */
  sellAmount: string;
  /** Which sell-fraction button triggered the estimation; surfaced via `mutation.variables` so the UI can target the right button. */
  fraction: BalanceFraction;
};

/**
 * Imperative React Query mutation that estimates the worst-case native-token
 * fee reserve before populating the sell input from a Max/50% click.
 *
 * Encapsulates URL building and the `/api/swap/price` request, then delegates
 * the per-chain reserve math to `computeNativeFeeReserveWei` so the Max/50%
 * path and the swap validator share an identical reserve.
 */
export function useEstimateNativeFeeReservePrice({
  buyToken,
  sellToken,
  chainId,
  slippage,
  excludedSources,
  accountAddress,
  buyTokenAmountUsd,
}: Params) {
  return useMutation<number, Error, EstimateNativeFeeReserveVariables>({
    mutationFn: async ({ sellAmount }) => {
      const params = new URLSearchParams({
        sellAmount,
        buyToken: buyToken?.address ?? '',
        sellToken: sellToken?.address ?? '',
        chainId: chainId.toString(),
      });
      if (typeof slippage !== 'undefined') {
        params.append('slippageBps', new Decimal(slippage).mul(1e4).toInteger().toString());
      }
      if (excludedSources) {
        params.append('excludedSources', excludedSources);
      }
      if (accountAddress) {
        params.append('taker', accountAddress);
      }

      const url = `${routes.api.SWAP_PRICE}?${params.toString()}`;
      const context = {
        buy_token_address: buyToken?.address,
        buy_token_amount: undefined,
        buy_token_symbol: buyToken?.symbol,
        chain_id: chainId,
        order_type: 'market_standard',
        pair: getTokenPair(sellToken, buyToken),
        sell_token_address: sellToken?.address,
        sell_token_amount: sellAmount,
        sell_token_symbol: sellToken?.symbol,
        usd_dollar_amount: buyTokenAmountUsd,
        wallet_connected_address: accountAddress ?? null,
      };

      const updatedPrice = (await zeroExApiFetcher(url, context)) as SwapPrice;
      const reserveWei = computeNativeFeeReserveWei(
        BigInt(updatedPrice.totalNetworkFee),
        chainId,
      );
      return Number(formatEther(reserveWei));
    },
  });
}
